import React from "react";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { ProblemFormatOptions } from "../Constants.ts";
import { labelFor } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * Picks what kind of problem the sheet is built from.
 *
 * These are mutually exclusive by nature - a sheet is math facts or word
 * problems or fractions or money - which is why the setting is a single value
 * rather than a flag per format. It used to be three booleans kept in step by
 * this handler, and a sheet that was somehow two of them at once was a state
 * the generator had no way to render.
 *
 * Always a native <select> now (ADR 0061 §3). ADR 0031 made it one below 500px
 * because the button bar stacked into a 611px column there; at 26 formats the
 * bar was the widest thing on the page at every width, and in a 308px rail it
 * would be taller than the rest of the rail combined. A select is one row, so
 * the picker can sit in the basic tier where a visitor finds out the site does
 * more than arithmetic. Native rather than PrimeReact's Dropdown: the platform
 * picker is the better control on a phone, and the option list is plain text.
 */
export const FormatSettings = React.memo(function FormatSettings() {
    const {getSettings, setSettings} = useSettingsActions();
    const {format: current, language} = useSetting((settings) => ({
        format: settings.Format,
        language: settings.Language,
    }));
    const t = useTranslation();

    function handleFormatSelection(value: ProblemFormatOptions) {
        // A sheet has to be some format; an empty value is never offered, but
        // is ignored rather than trusted if one ever arrives.
        if (!value) {
            return;
        }
        setSettings({ ...getSettings(), Format: value });
    }

    return(
        <div className='field formatField'>
            <label htmlFor='problemFormat'>{t('workbench.problemFormat')}</label>
            <select
                id='problemFormat'
                className='input formatSelect'
                value={current}
                onChange={(e) => handleFormatSelection(e.target.value as ProblemFormatOptions)}
            >
                { Object.values(ProblemFormatOptions).map((format) => (
                    <option key={format} value={format}>{labelFor(format, language)}</option>
                )) }
            </select>
        </div>
    );
});
